import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, ChevronLeft } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useEsAdmin } from "@/hooks/useEsAdmin";

export function DetailLayout({
  title,
  subtitle,
  backTo,
  backLabel,
  badges,
  rawData,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  backTo: string;
  backLabel?: string;
  badges?: ReactNode;
  rawData?: unknown;
  children: ReactNode;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const esAdmin = useEsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  // location.key vale "default" sólo en la primera entrada del historial (URL directa o
  // refresh). Cualquier navegación interna dentro de la SPA le asigna un hash distinto,
  // así que ese es nuestro indicador fiable de "hay historial al cual volver".
  function handleBack() {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(backTo);
    }
  }
  return (
    <div className="space-y-4">
      <div>
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-primary-strong"
        >
          <ChevronLeft className="h-4 w-4" />
          {backLabel ?? "Volver"}
        </button>
      </div>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-fg-muted">{subtitle}</p>}
        </div>
        {badges && <div className="flex flex-wrap gap-2">{badges}</div>}
      </header>
      <div className="space-y-4">{children}</div>
      {/* Herramienta de diagnóstico: solo para ADMIN_SICEN (confunde a usuarios finales) */}
      {esAdmin && rawData !== undefined && (
        <details
          className="rounded-lg border bg-surface p-3 text-xs"
          open={showRaw}
          onToggle={(e) => setShowRaw((e.target as HTMLDetailsElement).open)}
        >
          <summary className="cursor-pointer text-fg-subtle">Ver JSON crudo</summary>
          <pre className="mt-2 overflow-auto rounded bg-surface-2 p-2">
            {JSON.stringify(rawData, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border bg-surface shadow-sm">
      <h2 className="border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
        {title}
      </h2>
      <div className="grid gap-x-6 gap-y-3 p-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  );
}

export function Field({
  label,
  value,
  mono = false,
  span = 1,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  span?: 1 | 2 | 3;
}) {
  const colSpan = span === 3 ? "sm:col-span-2 lg:col-span-3" : span === 2 ? "sm:col-span-2" : "";
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div className={colSpan}>
      <div className="text-xs uppercase tracking-wide text-fg-subtle">{label}</div>
      <div className={`mt-0.5 text-sm text-fg ${mono ? "font-mono text-xs" : ""}`}>
        {isEmpty ? <span className="text-fg-faint">—</span> : value}
      </div>
    </div>
  );
}

export function StatusBadge({
  tone,
  children,
  title,
}: {
  tone: "ok" | "info" | "warn" | "neutral";
  children: ReactNode;
  /** Tooltip nativo; útil cuando la insignia muestra una forma abreviada. */
  title?: string;
}) {
  const cls = {
    ok: "bg-estado-ok-bg text-estado-ok",
    info: "bg-estado-info-bg text-estado-info",
    warn: "bg-estado-warn-bg text-estado-warn",
    neutral: "bg-surface-2 text-fg-muted",
  }[tone];
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`} title={title}>
      {children}
    </span>
  );
}

export function DetailLoading() {
  return <div className="rounded-lg border bg-surface p-6 text-sm text-fg-subtle">Cargando detalle…</div>;
}

export function DetailError({ message }: { message?: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-4 text-sm text-estado-warn">
      <AlertCircle className="mt-0.5 h-4 w-4" />
      <div>{message ?? "Error cargando el detalle."}</div>
    </div>
  );
}

export function formatDate(iso: string | null | undefined, includeTime = false): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const date = d.toISOString().slice(0, 10);
    if (!includeTime) return date;
    const time = d.toTimeString().slice(0, 5);
    return `${date} ${time}`;
  } catch {
    return iso;
  }
}
